import React, { useEffect, useMemo, useState } from 'react';
import PageWrapper from '../../components/PageWrapper';
import useAuth, { useAuthEffects } from '../../components/Auth';
import { useTheme, Box, Grid2, Button, Typography, FormControlLabel, Switch, Dialog, DialogTitle, DialogActions, DialogContent, Alert, Snackbar, Divider, useMediaQuery, Chip } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuickOrderPublishMutation, useQuickOrderUnpublishMutation, useQuickOrderRemoveMutation, useQuickOrderInfoQuery } from '../../services/quickOrders';
import SalesOrderAddProductDialog from './components/SalesOrderAddProductDialog';
import { ResourceListItem, QuickOrderProduct, CURRENCY_SYMBOLS } from '../../types';
import { PlanListItemElV2 } from '../../components/PlanList';
import { useQuickOrderAddExistingProductMutation, useQuickOrderAddNewProductMutation, useQuickOrderDeleteProductMutation, useQuickOrderEditProductMutation } from '../../services/quickOrderProducts';
import { SimpleList, SimpleListItem } from '../../components/SimpleList';
import { QuickOrderAddAdditionalFeeDialog } from './components/QuickOrderAddAdditionalFeeDialog';
import { useCreateQuickOrderAdditionalFeeMutation, useDeleteQuickOrderAdditionalFeeMutation } from '../../services/quickOrderAdditionalFees';
import { Currencies, FeeType } from '../../types';
import QuickOrderEditProductDialog from './components/QuickOrderEditProductDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { CloudDownload as CloudDownloadIcon, SyncLock as SyncLockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { translateToolName, translateUOM } from '../../i18n/utils';
import AddIcon from '@mui/icons-material/Add';
import { useGetCompanyVendorProfileQuery } from '../../services/vendorProfile';

const QuickOrderDetailView = () => {
	useAuthEffects();
	const {t} = useTranslation();
	const {id} = useParams();
	const theme = useTheme();
	const {userProfile} = useAuth();
	const [quickOrderAddExistingProductMutation, {isSuccess: isAddExistingProductSuccess}] = useQuickOrderAddExistingProductMutation();
	const [quickOrderAddNewProductMutation, {isSuccess: isAddNewProductSuccess}] = useQuickOrderAddNewProductMutation();
	const [quickOrderDeleteProductMutation] = useQuickOrderDeleteProductMutation();
	const [quickOrderEditProductMutation] = useQuickOrderEditProductMutation();
	const [quickOrderRemoveMutation, {error: deleteError, isSuccess: isDeleteSuccess}] = useQuickOrderRemoveMutation();
	const [quickOrderPublishMutation] = useQuickOrderPublishMutation();
	const [quickOrderUnpublishMutation] = useQuickOrderUnpublishMutation();
	const {data: quickOrder, isLoading: isQuickOrderInfoLoading, error: quickOrderInfoError} = useQuickOrderInfoQuery(id as string, {skip: !id || !userProfile});
	const navigate = useNavigate();
	const [selectedResource, setSelectedResource] = React.useState<ResourceListItem | null>(null);
	const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);
	const [isEditProductDialogOpen, setIsEditProductDialogOpen] = useState(false);
	const [isAddAdditionalFeeDialogOpen, setIsAddAdditionalFeeDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [createAdditionalFeeMutation] = useCreateQuickOrderAdditionalFeeMutation();
	const [deleteAdditionalFeeMutation] = useDeleteQuickOrderAdditionalFeeMutation();
	const [selectedProduct, setSelectedProduct] = useState<QuickOrderProduct | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const {data: vendorProfile} = useGetCompanyVendorProfileQuery(undefined, {skip: !userProfile});
	const products = useMemo(() => quickOrder?.products ?? [], [quickOrder]);
	const resources = useMemo(() => quickOrder?.resources ?? [], [quickOrder]);
	const additionalFees = useMemo(() => quickOrder?.additionalFees ?? [], [quickOrder]);

	useEffect(() => {
		if (deleteError) {
			setErrorMessage(`Failed to delete shopping list: ${(deleteError as any)?.data?.message}`);
		}
	}, [deleteError]);

	useEffect(() => {
		if (isDeleteSuccess) {
			navigate('/build-kits');
		}
	}, [isDeleteSuccess]);

	useEffect(() => {
		if (isAddExistingProductSuccess || isAddNewProductSuccess) {
			setSelectedResource(null);
		}
	}, [isAddExistingProductSuccess, isAddNewProductSuccess]);

	const handleResourceListDeleteBtnClick = (orderProductId: number) => {
		if (id) {
			quickOrderDeleteProductMutation(orderProductId.toString());
		}
	};

	const handleIsPublishedChange = (isPublished: boolean) => {
		if (id) {
			if (isPublished) {
				quickOrderPublishMutation(id);
			} else {
				quickOrderUnpublishMutation(id);
			}
		}
	};

	const handleAddProductSubmit = (payload: any, isExistingProduct: boolean) => {
		if (id) {
			if (isExistingProduct) {
				quickOrderAddExistingProductMutation({...payload, quickOrderId: Number(id)});
			} else {
				quickOrderAddNewProductMutation({...payload, quickOrderId: Number(id)});
			}
			setIsAddProductDialogOpen(false);
		}
	};

	const handleRemoveQuickOrderClick = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (id) {
			quickOrderRemoveMutation(id);
			setIsDeleteDialogOpen(false);
		}
	};

	const handleAdditionalFeesAddBtnClick = () => {
		setIsAddAdditionalFeeDialogOpen(true);
	};

	const handleAdditionalFeeDeleteBtnClick = (feeId: number) => {
		if (id) {
			deleteAdditionalFeeMutation(feeId);
		}
	};

	const handleAdditionalFeeSubmit = async (name: string, price: number, type: FeeType) => {
		if (!quickOrder) return;
		await createAdditionalFeeMutation({
			quickOrderId: quickOrder.id,
			name,
			price: price.toString(),
			type,
		});
	};

	const handleEditProductSubmit = async (payload: Partial<QuickOrderProduct> & {isBundled?: boolean}) => {
		if (id && selectedProduct) {
			await quickOrderEditProductMutation({
				id: selectedProduct.id,
				uom: payload.uom,
				quantity: payload.quantity,
				pricePerUnit: payload.pricePerUnit,
				isBundled: payload.isBundled,
			}).unwrap()
				.then(() => {
					setIsEditProductDialogOpen(false);
				})
				.catch((e) => {
					setErrorMessage((e as any)?.data?.message ?? 'Failed to update product');
				});
		}
	};

	const handleProductEditBtnClick = (product: QuickOrderProduct) => {
		setSelectedProduct(product);
		setIsEditProductDialogOpen(true);
	};

	const handleProductAddBtnClick = (resource: ResourceListItem | null) => {
		setSelectedResource(resource);
		setIsAddProductDialogOpen(true);
	};

	const orphanedProducts = useMemo(() => products.filter(({resourceId}) => !resources.find(({id}) => id === resourceId)), [products, resources]);
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

	return <PageWrapper maxWidth='md' isLoading={isQuickOrderInfoLoading} error={quickOrderInfoError}>
		<Box display='flex' flexDirection='column' gap={2} mt={2}>
			<div>
				<Button startIcon={<ChevronLeftIcon />} component={Link} to='/build-kits'>Back to build kits</Button>
			</div>
			{/* My shopping list for: <br/> */}
			<Typography variant='h6'>Project</Typography>
			{quickOrder?.plan && <Grid2 container spacing={2}>
				<Grid2 size={{xs: 12, sm: 6, md: 4}}>
					<Box mt={1}>
						<PlanListItemElV2 item={quickOrder.plan} />
					</Box>
				</Grid2>
			</Grid2>}
			<Box display='flex' flexDirection='column' py={2} gap={1}>
				<Typography variant='h6' textAlign='center'>
					Recipe
				</Typography>
				{resources.map((resource) => <Box key={resource.id} mb={3}>
					<SimpleListItem 
						imgUrl={resource.imgUrl}
						label={resource.isTool ? translateToolName(resource.name) : resource.name} 
						properties={[...(resource.isOptional ? [{label: 'Optional'}] : []), {
							label: resource.quantity,
							secondary: resource.uom,
						}
						]} />
					<SimpleList 
						actions={[
							{
								label: 'Add item',
								icon: <AddIcon />,
								onClick: () => handleProductAddBtnClick(resource),
								sx: {
									px: 2,
									backgroundColor: 'background.paper',
									'&:hover': {
										backgroundColor: 'divider',
									},
								},
							}
						]}
						items={products.filter(({resourceId}) => resourceId === resource.id).map((product) => ({
							imgUrl: product.imgUrl,
							label: product.name,
							properties: [...(product.bundleProductId ? [{
								el: <Chip label='B' />,
							}] : []), ...(isMdUp ? [{
								label: product.sku,
							}] : []), {
								label: product.quantity,
								secondary: translateUOM(product.uom),
							}, {
								label: `${CURRENCY_SYMBOLS[vendorProfile?.currency as Currencies]}${Number(product.pricePerUnit).toFixed(2)}`,
								secondary: `${t('productView.perUnit')}`,
							}],
							href: product.quickOrderId === quickOrder?.id ? `/products/${product.productId}` : undefined,
							sx: {
								filter: product.quickOrderId !== quickOrder?.id ? 'opacity(0.5)' : 'none',
							},
							actions: [{
								label: 'Edit',
								icon: <EditIcon />,
								onClick: () => handleProductEditBtnClick(product),
								disabled: product.quickOrderId !== quickOrder?.id,
							},{
								label: 'Remove',
								icon: <DeleteIcon />,
								onClick: () => handleResourceListDeleteBtnClick(product.id),
								disabled: product.quickOrderId !== quickOrder?.id,
							}],
							additionalInfo: product.digitalDelivery ? <Box display='flex' alignItems='center' gap={1} mt={1}>
								<Box display='flex' alignItems='center' gap={1}>
									<Box flex='auto' display='flex' alignItems='center' gap={1}>
										<CloudDownloadIcon sx={{ fontSize: 16, color: 'success.main' }} />
										<Typography variant='body2' color='text.secondary'>{t('productView.digitalDelivery', { count: Number(product.documentsCount) })}</Typography>
									</Box>
									{product.quickOrderId !== quickOrder?.id && <Box flex='auto' display='flex' alignItems='center' gap={1}>
										<SyncLockIcon />
										<Typography variant='body2' color='text.secondary'>{t('productView.isExtended')}</Typography>
									</Box>}
								</Box>
							</Box> : null,
						}))} />
				</Box>)}
			</Box>
			<Box display='flex' flexDirection='column' py={2} gap={1}>
				{/* <Box sx={{mt: 4, mb: 2}}> */}
				<Typography variant='h6'>
					Other products
				</Typography>
				{/* </Box> */}
				<SimpleList 
					actions={[
						{
							label: 'Add item',
							icon: <AddIcon />,
							onClick: () => handleProductAddBtnClick(null),
							sx: {
								px: 2,
								backgroundColor: 'background.paper',
								'&:hover': {
									backgroundColor: 'divider',
								},
							},
						}
					]}
					items={orphanedProducts.map((product) => ({
						imgUrl: product.imgUrl,
						label: product.name,
						properties: [{
							label: product.sku,
						}, {
							label: product.quantity,
							secondary: product.uom,
						}, {
							label: `${CURRENCY_SYMBOLS[vendorProfile?.currency as Currencies]}${Number(product.pricePerUnit).toFixed(2)}`,
							secondary: `${t('productView.perUnit')}`,
						}],
						href: `/products/${product.productId}`,
						actions: [{
							label: 'Edit',
							icon: <EditIcon />,
							onClick: () => handleProductEditBtnClick(product),
							disabled: product.quickOrderId !== quickOrder?.id,
						},{
							label: 'Remove',
							icon: <DeleteIcon />,
							onClick: () => handleResourceListDeleteBtnClick(product.id),
							disabled: product.quickOrderId !== quickOrder?.id,
						}],
					}))} 
				/>
			</Box>
			
			{additionalFees && <Box display='flex' flexDirection='column' py={2} gap={1}>
				<Typography variant='h6'>
					Additional fees
				</Typography>
				<SimpleList
					items={additionalFees.map((fee) => ({
						label: fee.name,
						properties: [{
							label: `${CURRENCY_SYMBOLS[vendorProfile?.currency as Currencies]}${Number(fee.price).toFixed(2)}`,
						}],
						onDeleteClick: () => handleAdditionalFeeDeleteBtnClick(fee.id)
					}))}
					actions={[
						{
							label: 'Add item',
							icon: <AddIcon />,
							onClick: () => handleAdditionalFeesAddBtnClick(),
							sx: {
								px: 2,
								backgroundColor: 'background.paper',
								'&:hover': {
									backgroundColor: 'divider',
								},
							},
						}
					]}
				/>
			</Box>}
			
			{quickOrder && <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, py: 2}}>
				<Typography variant='h6' textAlign='center'>Settings</Typography>
				<FormControlLabel
					control={<Switch checked={!!quickOrder?.isBundle} disabled />}
					label='Is sold as a bundle'
				/>
				<FormControlLabel
					control={<Switch checked={quickOrder?.publishedAt !== null} onChange={(e) => handleIsPublishedChange(e.target.checked)} />}
					label='Is published'
				/>
				<Divider />
				<Box>
					<Button size='large' variant='text' color='error' startIcon={<DeleteIcon />} onClick={() => setIsDeleteDialogOpen(true)}>Delete kit</Button>
				</Box>
			</Box>}
		</Box>
		<SalesOrderAddProductDialog
			open={isAddProductDialogOpen}
			resource={selectedResource}
			onSubmit={handleAddProductSubmit}
			onClose={() => setIsAddProductDialogOpen(false)}
		/>
		<QuickOrderAddAdditionalFeeDialog
			open={isAddAdditionalFeeDialogOpen}
			onClose={() => setIsAddAdditionalFeeDialogOpen(false)}
			onSubmit={handleAdditionalFeeSubmit}
		/>
		<QuickOrderEditProductDialog
			open={isEditProductDialogOpen}
			onClose={() => setIsEditProductDialogOpen(false)}
			onSubmit={handleEditProductSubmit}
			product={selectedProduct}
		/>
		<Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
			<form onSubmit={handleRemoveQuickOrderClick}>
				<DialogTitle>Delete build kit</DialogTitle>
				<DialogContent>
					<Typography>Are you sure you want to delete build kit for <strong>{quickOrder?.plan?.title}</strong>?</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
					<Button type='submit' variant='contained' color='error'>Delete</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => setErrorMessage(null)}>
			<Alert severity='error'>{errorMessage}</Alert>
		</Snackbar>
	</PageWrapper>;
};

export default QuickOrderDetailView;
